import { EMPRESA } from '@/lib/empresa';
import type {
  FinanceCiclo,
  FinanceData,
  FinanceOrigem,
  FinancePeriod,
} from '@/types/finance.types';
import { imprimirHtml, salvarArquivo } from '@/lib/arquivos';

const PERIODO_TITULO: Record<FinancePeriod, string> = {
  mes: 'Mês corrente',
  trimestre: 'Últimos 3 meses',
  ano: 'Últimos 12 meses',
};

export const ORIGEM_LABEL: Record<FinanceOrigem, string> = {
  apple: 'App Store',
  google: 'Google Play',
  painel: 'Painel',
  abacatepay: 'AbacatePay',
  manual: 'Baixa manual',
};

const CICLO_LABEL: Record<FinanceCiclo, string> = {
  mensal: 'mensal',
  anual: 'anual',
  contrato: 'contrato',
  avulsa: 'avulsa',
};

/** "Profissional · anual". Sem ciclo (API antiga), só o plano. */
export function planoComCiclo(plano: string | null, ciclo?: FinanceCiclo): string {
  return [plano, ciclo && CICLO_LABEL[ciclo]].filter(Boolean).join(' · ') || '—';
}

/** Sufixo do valor de uma renovação: "/mês", "/ano" ou nada no contrato. */
export function sufixoCiclo(ciclo?: FinanceCiclo): string {
  return ciclo === 'anual' ? '/ano' : ciclo === 'contrato' || ciclo === 'avulsa' ? '' : '/mês';
}

const brl = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dataBR = (iso: string) => new Date(iso).toLocaleDateString('pt-BR');
// Início do período vem como meia-noite UTC do dia 1: formatado no fuso local
// cairia no último dia do mês anterior.
const dataBRUtc = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
const pad2 = (n: number) => String(n).padStart(2, '0');

const esc = (s: unknown) =>
  String(s ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c
  );

function intervalo(d: FinanceData): string {
  const inicio = d.recebido?.desde ? dataBRUtc(d.recebido.desde) : null;
  const fim = dataBR(d.geradoEm);
  return inicio ? `${inicio} a ${fim}` : `até ${fim}`;
}

function nomeArquivo(d: FinanceData, ext: string): string {
  const g = new Date(d.geradoEm);
  return `relatorio-financeiro-${d.period}-${g.getFullYear()}-${pad2(g.getMonth() + 1)}-${pad2(g.getDate())}.${ext}`;
}

/** Linhas do relatório em CSV: cabeçalho da empresa + seções. Separador ";" (Excel pt-BR). */
export function linhasCsv(d: FinanceData): (string | number)[][] {
  const r = d.recebido;
  const ret = d.retencao;
  return [
    [EMPRESA.razaoSocial],
    ['CNPJ', EMPRESA.cnpj],
    ['Endereço', `${EMPRESA.endereco}, ${EMPRESA.cidade}, CEP ${EMPRESA.cep}`],
    ['Relatório financeiro', PERIODO_TITULO[d.period]],
    ['Período', intervalo(d)],
    ['Gerado em', new Date(d.geradoEm).toLocaleString('pt-BR')],
    [],
    ['Resumo'],
    ['MRR (R$)', num(d.mrr.totalCentavos)],
    ['ARR (R$)', num(d.arrCentavos ?? d.mrr.totalCentavos * 12)],
    ...(r
      ? [
          ['Recebido bruto (R$)', num(r.totalCentavos)],
          ['Recebido líquido estimado (R$)', num(r.liquidoCentavos ?? r.totalCentavos)],
          ['Pagamentos no período', r.pagamentos],
        ]
      : []),
    ['Pagantes hoje', `${d.pagantes.count} de ${d.pagantes.totalUsers}`],
    ['Ticket médio (R$)', num(d.ticketMedioCentavos)],
    ...(ret
      ? [
          ['Cancelamentos no período', ret.cancelamentos],
          ['Testes grátis iniciados', ret.testesIniciados],
          ['Testes convertidos em pagamento', ret.testesConvertidos],
        ]
      : []),
    [],
    ['Pagamentos'],
    ['Data', 'Cliente', 'Origem', 'Plano', 'Bruto (R$)', 'Líquido estimado (R$)'],
    ...(r?.itens ?? []).map((pg) => [
      dataBR(pg.data),
      pg.cliente ?? `Usuário ${pg.userId}`,
      ORIGEM_LABEL[pg.origem],
      planoComCiclo(pg.plano, pg.ciclo),
      num(pg.valorCentavos),
      num(pg.liquidoCentavos ?? pg.valorCentavos),
    ]),
    [],
    ['Planos'],
    ['Plano', 'Preço', 'Assinantes', 'Pagantes', 'Receita/mês (R$)', '% do MRR'],
    ...d.planos.map((p) => [
      p.nome,
      p.precoLabel,
      p.assinantes ?? '',
      p.pagantes ?? '',
      num(p.receitaMesCentavos),
      `${p.pctMrr}%`,
    ]),
    [],
    ['Vencem nos próximos 30 dias'],
    ['Vence em', 'Cliente', 'Origem', 'Plano', 'Próxima cobrança (R$)'],
    ...(d.vencendo ?? []).map((v) => [
      dataBR(v.venceEm),
      v.cliente ?? `Usuário ${v.userId}`,
      ORIGEM_LABEL[v.origem],
      planoComCiclo(v.plano, v.ciclo),
      num(v.valorCentavos),
    ]),
  ];
}

export function baixarRelatorioCsv(d: FinanceData) {
  const csv = linhasCsv(d)
    .map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  // BOM pro Excel abrir com acento correto.
  const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8;' });
  void salvarArquivo(blob, nomeArquivo(d, 'csv'));
}

/**
 * Relatório em PDF: abre uma janela com o documento e chama a impressão do
 * navegador ("Salvar como PDF"), no mesmo padrão da proposta de orçamento.
 */
export function abrirRelatorioPdf(d: FinanceData) {
  const origin = window.location.origin;
  const r = d.recebido;
  const ret = d.retencao;
  const gerado = new Date(d.geradoEm);

  const kpis: [string, string, string?][] = [
    ['MRR', brl(d.mrr.totalCentavos), 'receita recorrente mensal'],
    ['ARR', brl(d.arrCentavos ?? d.mrr.totalCentavos * 12), 'MRR x 12'],
    ...(r
      ? ([
          ['Recebido bruto', brl(r.totalCentavos), `${r.pagamentos} pagamento(s)`],
          [
            'Recebido líquido',
            brl(r.liquidoCentavos ?? r.totalCentavos),
            'estimado, após taxa das lojas',
          ],
        ] as [string, string, string][])
      : []),
    ['Pagantes hoje', `${d.pagantes.count}`, `de ${d.pagantes.totalUsers} usuários`],
    ['Ticket médio', brl(d.ticketMedioCentavos), 'por pagante/mês'],
    ...(ret
      ? ([
          ['Cancelamentos', `${ret.cancelamentos}`, 'no período'],
          [
            'Conversão de teste',
            ret.conversaoPct == null ? '-' : `${ret.conversaoPct}%`,
            `${ret.testesConvertidos} de ${ret.testesIniciados} testes grátis`,
          ],
        ] as [string, string, string][])
      : []),
  ];

  const linhasPagamentos = (r?.itens ?? [])
    .map(
      (pg) =>
        `<tr><td>${dataBR(pg.data)}</td><td>${esc(pg.cliente ?? `Usuário ${pg.userId}`)}</td><td>${ORIGEM_LABEL[pg.origem]}</td><td>${esc(planoComCiclo(pg.plano, pg.ciclo))}</td><td class="num">${brl(pg.valorCentavos)}</td><td class="num">${brl(pg.liquidoCentavos ?? pg.valorCentavos)}</td></tr>`
    )
    .join('');

  const linhasPlanos = d.planos
    .map(
      (p) =>
        `<tr><td>${esc(p.nome)}</td><td>${esc(p.precoLabel)}</td><td class="num">${p.assinantes ?? '-'}</td><td class="num">${p.pagantes ?? '-'}</td><td class="num">${brl(p.receitaMesCentavos)}</td><td class="num">${p.pctMrr}%</td></tr>`
    )
    .join('');

  const linhasVencendo = (d.vencendo ?? [])
    .map(
      (v) =>
        `<tr><td>${dataBR(v.venceEm)}</td><td>${esc(v.cliente ?? `Usuário ${v.userId}`)}</td><td>${ORIGEM_LABEL[v.origem]}</td><td>${esc(planoComCiclo(v.plano, v.ciclo))}</td><td class="num">${brl(v.valorCentavos)}</td></tr>`
    )
    .join('');

  const vazio = (cols: number, texto: string) =>
    `<tr><td colspan="${cols}" class="empty">${texto}</td></tr>`;

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Relatório financeiro ${esc(EMPRESA.nomeFantasia)} ${dataBR(d.geradoEm)}</title>
<style>
  :root{ --navy:#0b3d5c; --ink:#1a1f24; --muted:#6b7580; --line:#e3e8ec; --soft:#f4f6f8; }
  *{ box-sizing:border-box; }
  html,body{ margin:0; }
  @page{ size:A4; margin:14mm; }
  body{ font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif; color:var(--ink);
    font-size:11.5px; line-height:1.4; padding:28px 34px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .serif{ font-family:Georgia,"Times New Roman",serif; }
  .top{ display:flex; justify-content:space-between; align-items:flex-start; gap:24px; }
  .brand{ display:flex; align-items:center; gap:12px; }
  .brand img{ height:44px; }
  .brand .name{ font-size:18px; font-weight:700; letter-spacing:2px; }
  .brand .tag{ font-size:10px; color:var(--muted); letter-spacing:.3px; }
  .company{ text-align:right; font-size:10.5px; color:var(--muted); line-height:1.55; }
  .company b{ color:var(--ink); font-weight:600; }
  .rule{ height:2px; background:var(--navy); margin:12px 0 14px; }
  h1{ font-size:19px; color:var(--navy); margin:0 0 2px; font-weight:600; }
  .subtitle{ color:var(--muted); font-size:11.5px; margin-bottom:14px; }
  h2{ font-size:10.5px; text-transform:uppercase; letter-spacing:1.2px; color:var(--navy); margin:16px 0 6px; font-weight:700; }
  .kpis{ display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
  .kpi{ background:var(--soft); border-radius:6px; padding:8px 10px; break-inside:avoid; }
  .kpi .l{ font-size:10px; color:var(--muted); }
  .kpi .v{ font-size:15px; font-weight:700; margin-top:1px; }
  .kpi .h{ font-size:9.5px; color:var(--muted); margin-top:1px; }
  table{ width:100%; border-collapse:collapse; }
  th{ text-align:left; font-size:9.5px; text-transform:uppercase; letter-spacing:.6px; color:var(--muted);
    border-bottom:1.5px solid var(--line); padding:6px 4px; font-weight:600; }
  td{ padding:5px 4px; border-bottom:1px solid var(--line); }
  tr{ break-inside:avoid; }
  .num{ text-align:right; white-space:nowrap; }
  .empty{ color:var(--muted); text-align:center; padding:10px 4px; }
  .notes{ margin-top:16px; font-size:9.5px; color:var(--muted); line-height:1.5; }
  .foot{ margin-top:14px; padding-top:8px; border-top:1px solid var(--line); font-size:9.5px; color:var(--muted);
    display:flex; justify-content:space-between; }
  @media print{ body{ padding:0; } }
</style></head>
<body onload="window.print()">
  <div class="top">
    <div class="brand">
      <img src="${origin}/logo.png" alt="${esc(EMPRESA.nomeFantasia)}">
      <div>
        <div class="name serif">CURRAL TECH</div>
        <div class="tag">GESTÃO PECUÁRIA INTELIGENTE</div>
      </div>
    </div>
    <div class="company">
      <b>${esc(EMPRESA.razaoSocial)}</b><br>
      CNPJ ${esc(EMPRESA.cnpj)}<br>
      ${esc(EMPRESA.endereco)}<br>
      ${esc(EMPRESA.cidade)}, CEP ${esc(EMPRESA.cep)}<br>
      ${esc(EMPRESA.email)}
    </div>
  </div>

  <div class="rule"></div>

  <h1 class="serif">Relatório financeiro</h1>
  <div class="subtitle">${PERIODO_TITULO[d.period]}, ${intervalo(d)}. Valores em reais.</div>

  <h2>Resumo</h2>
  <div class="kpis">
    ${kpis.map(([l, v, h]) => `<div class="kpi"><div class="l">${esc(l)}</div><div class="v">${esc(v)}</div>${h ? `<div class="h">${esc(h)}</div>` : ''}</div>`).join('')}
  </div>

  <h2>Pagamentos no período</h2>
  <table>
    <thead><tr><th>Data</th><th>Cliente</th><th>Origem</th><th>Plano</th><th class="num">Bruto</th><th class="num">Líquido est.</th></tr></thead>
    <tbody>${linhasPagamentos || vazio(6, 'Nenhum pagamento registrado no período.')}</tbody>
  </table>

  <h2>Planos</h2>
  <table>
    <thead><tr><th>Plano</th><th>Preço</th><th class="num">Assinantes</th><th class="num">Pagantes</th><th class="num">Receita/mês</th><th class="num">% do MRR</th></tr></thead>
    <tbody>${linhasPlanos}</tbody>
  </table>

  <h2>Vencem nos próximos 30 dias</h2>
  <table>
    <thead><tr><th>Vence em</th><th>Cliente</th><th>Origem</th><th>Plano</th><th class="num">Próxima cobrança</th></tr></thead>
    <tbody>${linhasVencendo || vazio(5, 'Nenhuma assinatura paga vence nos próximos 30 dias.')}</tbody>
  </table>

  <div class="notes">
    Recebido conta compras e renovações pagas nas lojas e assinaturas pagas criadas no painel; teste grátis,
    cortesia e compras de teste não entram. O líquido é estimado: na App Store pela proporção medida no
    relatório de vendas da conta (R$ 39,90 pagos, R$ 28,49 repassados), no Google Play pela taxa de 15% para
    assinaturas, e sem taxa no que é cobrado pelo painel. Conferir com os relatórios das lojas no fechamento.
  </div>

  <div class="foot">
    <span>${esc(EMPRESA.razaoSocial)}, CNPJ ${esc(EMPRESA.cnpj)}</span>
    <span>Gerado em ${gerado.toLocaleString('pt-BR')}</span>
  </div>
</body></html>`;

  void imprimirHtml(html, nomeArquivo(d, 'pdf'), 'width=900,height=1100');
}
